import React, { createContext, useState, useEffect, useMemo } from 'react';
import { lightTheme, darkTheme, ThemeType } from '../shared/Theme';

const STORAGE_KEY = 'maritaca-dark-mode';

interface ThemeContextProps {
    isDarkMode: boolean;
    toggleDarkMode: () => void;
    currentTheme: ThemeType;
}

export const ThemeContext = createContext<ThemeContextProps>({
    isDarkMode: false,
    toggleDarkMode: () => {},
    currentTheme: lightTheme,
});

function getInitialDarkMode(): boolean {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
        return stored === 'true';
    }
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

export const ThemeContextProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState<boolean>(getInitialDarkMode);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, String(isDarkMode));
    }, [isDarkMode]);

    const toggleDarkMode = () => {
        document.body.classList.add('no-transitions');
        setIsDarkMode(prev => !prev);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                document.body.classList.remove('no-transitions');
            });
        });
    };

    const currentTheme = useMemo(() => isDarkMode ? darkTheme : lightTheme, [isDarkMode]);

    return (
        <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode, currentTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
